import clsx from 'clsx'

import { Flex, Text } from '@buffer-mono/popcorn'

import { MetricNumber } from '../MetricNumber'
import sharedStyles from '../chart-shared.module.css'

import { ViewMode } from '../MetricChart/types'
import styles from './MetricsNarrativeChart.module.css'

import type { CSSProperties, JSX } from 'react'

export interface SummaryItem {
  key: string
  title: string
  color: string
  /** Value for the primary (current) period. Null when not available. */
  primaryValue: number | null
  /** Value for the comparison period. Null when no comparison data. */
  comparisonValue: number | null
  /** Period-over-period delta. Set only in 'both' view mode with valid comparison. */
  differenceValue: number | null
  differenceMode: 'percentage' | 'absolute'
  /** Optional unit symbol appended after the value (e.g. '%' for growth rates). */
  unit?: string
  /**
   * How to mark this metric's comparison value, matching how the chart draws it:
   * `dashed` mirrors a dashed comparison line, `hatched` a hatched comparison
   * bar. Defaults to `dashed`.
   */
  comparisonSwatch?: 'dashed' | 'hatched'
}

function formatDiff(value: number, mode: 'percentage' | 'absolute'): string {
  const sign = value >= 0 ? '+' : ''
  if (mode === 'percentage') {
    // Use 1 decimal place for sub-1% changes so small but real deltas
    // (e.g. +0.09%) don't incorrectly round to "+0%".
    if (value !== 0 && Math.abs(value) < 1) {
      return `${sign}${value.toFixed(1)}%`
    }
    return `${sign}${Math.round(value)}%`
  }
  return `${sign}${Math.round(value).toLocaleString()}`
}

type ColorDotStyle = CSSProperties & { '--dot-color': string }

interface SummaryPanelProps {
  items: SummaryItem[]
  hasComparison: boolean
  viewMode: ViewMode
}

export function SummaryPanel({
  items,
  hasComparison,
  viewMode,
}: SummaryPanelProps): JSX.Element {
  return (
    <Flex align="start" gap="2xl" wrap="wrap" className={styles.summaryRow}>
      {items.map((item) => {
        if (item.primaryValue == null) return null
        const dotStyle: ColorDotStyle = { '--dot-color': item.color }
        const showDiff = item.differenceValue != null
        const comparisonValue =
          hasComparison && item.comparisonValue != null
            ? item.comparisonValue
            : null

        // Mark the comparison value the way the chart draws this series: diagonal
        // stripes for bars (posts), a dashed outline for lines (followers).
        const comparisonSwatchClass =
          item.comparisonSwatch === 'hatched'
            ? sharedStyles.colorDotHatched
            : sharedStyles.colorDotDashed

        return (
          <Flex key={item.key} direction="column" gap="sm">
            <Text className={styles.summaryMetricName}>{item.title}</Text>

            <Flex align="center" gap="md">
              {/* In Comparison view the single dot represents the comparison
                  mark, so it takes the comparison swatch too. */}
              <Flex align="center" gap="xs">
                <span
                  className={clsx(
                    sharedStyles.colorDot,
                    viewMode === ViewMode.Comparison && comparisonSwatchClass,
                  )}
                  aria-hidden="true"
                  style={dotStyle}
                />
                <p className={styles.summaryValue}>
                  {showDiff ? (
                    formatDiff(item.differenceValue ?? 0, item.differenceMode)
                  ) : (
                    <MetricNumber value={item.primaryValue} />
                  )}
                  {item.unit}
                </p>
              </Flex>

              {/* Comparison dot + value, grouped under the same label */}
              {comparisonValue != null && (
                <Flex align="center" gap="xs">
                  <span
                    className={clsx(
                      sharedStyles.colorDot,
                      comparisonSwatchClass,
                    )}
                    aria-hidden="true"
                    style={dotStyle}
                  />
                  <p
                    className={clsx(
                      styles.summaryValue,
                      styles.summaryValueSubtle,
                    )}
                  >
                    <MetricNumber value={comparisonValue} />
                    {item.unit}
                  </p>
                </Flex>
              )}
            </Flex>
          </Flex>
        )
      })}
    </Flex>
  )
}
